import {
  SignUpOutput,
  type AuthSession,
  type SignInOutput
} from 'aws-amplify/auth';
import jwt_decode from 'jwt-decode';
import moment, { Moment } from 'moment';
import * as Sentry from '@sentry/angular-ivy';
import { inject, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { General } from '@atomickit/atomickit-constants';
import { NavigationExtras, Router } from '@angular/router';
import { defer, from, Observable, throwError, zip } from 'rxjs';
import { catchError, switchMap, takeWhile, tap } from 'rxjs/operators';

import { AtomickitUser, CognitoUserInterface, CurrentUserInfo, User, UserInterface } from '@shared/meta-data';
import { HandleError, HttpErrorHandler } from '@shared/services/error-handler/http-error-handler.service';
import { AnalyticsManagerService } from '@app/utils/analytics-manager/analytics-manager.service';
import { BusinessProfileService } from '@services/business-profile/business-profile.service';
import { SalesTaxConfigService } from '@services/sales-tax-config/sales-tax-config.service';
import { ReferenceDataService } from '@services/reference-data/reference-data.service';
import { StorageService } from '@shared/services/storage/storage.service';
import { EventTypes } from '@shared/meta-data/analytics-manager.meta';
import { AppBanner } from '@shared/meta-data/app-banner.meta';
import { STORE_KEYS, USERTYPES } from '@app/utils/constants';
import AmplifyAuth from '@app/auth/amplify-auth';
import { AUTH_SYNC_MESSAGE_TYPE } from '@app/auth/auth-sync.messages';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { AppConfig } from '@app/app.config';
import * as formApp from '@app/@store/app/app.reducer';
import * as AppActions from '@app/@store/app/app.actions';
import { autoSignIn } from '@aws-amplify/auth';
import { GtmService, GtmEventType } from '@app/utils/gtm/gtm.service';
import { MetaPixelService } from '@app/utils/meta-pixel/meta-pixel.service';
import { isUsageBasedStripeBilling } from '@app/utils/billing-access';
@Injectable()
export class AuthService {
  redirectUrl?: string;
  passwordResetEmail: string;

  /** Removes window `message` listeners used for Admin / AutoFiler SPA auth handoff. */
  private adminAuthSyncDetach: (() => void) | null = null;

  private autofilerAuthSyncDetach: (() => void) | null = null;


  private readonly handleError: HandleError;

  private modalService: NgbModal = inject(NgbModal);
  private store: Store = inject(Store<formApp.AppState>);

  constructor(
    private readonly router: Router,
    private businessProfileService: BusinessProfileService,
    private referenceDataService: ReferenceDataService,
    private storage: StorageService,
    private analyticsManagerService: AnalyticsManagerService,
    private gtmService: GtmService,
    private metaPixelService: MetaPixelService,
    private readonly salesTaxConfigService: SalesTaxConfigService,
    httpErrorHandler: HttpErrorHandler
  ) {
    this.handleError = httpErrorHandler.createHandleError('Auth Service')
  }

  get user(): User {
    return this.isAuthenticated() && new User(this.getUserData());
  }

  get trialExpiryDate(): Moment {
    return moment(this.businessProfileService.businessProfile?.created_at).add(General.SUBSCRIPTION_TRIAL_PERIOD, 'days');
  }

  get trialHasExpired(): boolean {
    return this.businessProfileService.businessProfile?.account_status === General.ACCOUNT_STATUS.TRIAL_EXPIRED;
  }

  get isInTrial(): boolean {
    return this.businessProfileService.businessProfile?.account_status === General.ACCOUNT_STATUS.IN_TRIAL;
  }

  get subscriptionCancelled(): boolean {
    return this.businessProfileService.businessProfile?.account_status === General.ACCOUNT_STATUS.CANCELLED;
  }

  get subscriptionPendingCancellation(): boolean {
    return this.businessProfileService.businessProfile?.account_status === General.ACCOUNT_STATUS.PENDING_CANCELLATION;
  }


  get appBanner(): AppBanner | undefined {
    if (isUsageBasedStripeBilling(this.businessProfileService.businessProfile)) {
      return undefined;
    }

    switch (true) {
      case this.isInTrial:
        return {
          message: `Your trial expires ${this.trialExpiryDate.startOf('day').fromNow()}`,
          link: ['account-settings', 'plan'],
          hint: 'Click here to activate'
        };
    
      case this.subscriptionPendingCancellation:
        return {
          message: `Your subscription is at risk of cancellation, click here to fix`,
          link: ['account-settings', 'plan'],
          hint: ''
        };
    
      case this.subscriptionCancelled:
        return {
          message: `Your subscription has been cancelled, click here to subscribe`,
          link: ['account-settings', 'plan'],
          hint: '',
        };
    
      case this.trialHasExpired:
        return {
          message: `Your trial has expired, click here to subscribe`,
          link: ['account-settings', 'plan'],
          hint: '',
        };
    
      default:
        return undefined;
    }
  }

  get userInfo(): CurrentUserInfo {
    return {
      firstName: this.user?.given_name,
      lastName: this.user?.family_name,
      email: this.user?.email,
      businessName: this.businessProfileService.businessProfile?.business_name,
      hasMultipleBusinesses: this.hasMultipleBusinesses
    };
  }

  get hasCompletedOnboarding(): boolean {
    return !!this.businessProfileService.businessProfile?.business_profile_id;
  }

  get hasMultipleBusinesses(): boolean {
    return this.businessProfileService.businessProfiles?.length > 1;
  }

  get hasAccessibleBusinesses(): boolean {
    return this.businessProfileService.businessProfiles?.some(b => b.revoked_at === null);
  }

  /** Matches server `validateAdminRoutes` (`custom:UserType` → admin). */
  get isCognitoAdmin(): boolean {
    const raw = this.getUserData()?.['custom:UserType'];
    return typeof raw === 'string' && raw.toLowerCase() === USERTYPES.admin;
  }

  /** Matches server filing-operator middleware (`custom:UserType` → autofiling). */
  get isAutofilingOperator(): boolean {
    const raw = this.getUserData()?.['custom:UserType'];
    return typeof raw === 'string' && raw.toLowerCase() === USERTYPES.autofiling;
  }

  /** After login: show merchant vs Admin vs AutoFiler chooser (`/choose-account`). */
  get isOperatorAccountLandingUser(): boolean {
    return this.isCognitoAdmin || this.isAutofilingOperator;
  }

  static getTokenExpirationTimeValueInMilliseconds(token: string): number {
    try {
      const decoded = jwt_decode<any>(token);

      if (!decoded.exp) {
        return 0;
      }

      const date = new Date(0);
      date.setUTCSeconds(decoded.exp);

      return date.valueOf();
    } catch (e) {
      return 0;
    }
  }

  private static checkIfTokenIsExpiredOrValid(token: string): boolean {
    return AuthService.getTokenExpirationTimeValueInMilliseconds(token) > new Date().valueOf();
  }

  private static getTimeToTokenExpiry(token: string): number {
    return AuthService.getTokenExpirationTimeValueInMilliseconds(token) - new Date().valueOf();
  }

  handleAuthSuccess(credentials: Partial<UserInterface>): Observable<any> {
    StorageService.clearStoreKeys();

    this.storage.save(STORE_KEYS.USER_AUTH_KEY, credentials);
    this.logoutWhenTokenExpires();
    return this.loadAndCacheUserData();
  }

  getUserData(): UserInterface | undefined {
    return this.storage.get<UserInterface>(STORE_KEYS.USER_AUTH_KEY);
  }

  isAuthenticated(): boolean {
    return AuthService.checkIfTokenIsExpiredOrValid(this.getUserData()?.token);
  }

  getTokenAndValidity(): { token: string, isValid: boolean } {
    return {
      token: this.getUserData()?.token,
      isValid: this.isAuthenticated()
    };
  }

  /**
   * Opens the operator Admin SPA (`/auth/sync`) and listens for a postMessage handshake so the admin tab can reuse this Cognito session.
   * Use this from the navbar and choose-account flow; do not open `/auth/sync` without registering this listener.
   */
  openAdminConsoleWithAuthSync(): void {
    const adminOrigin = environment.adminAppOrigin.replace(/\/$/, '');
    const prefix = environment.adminAppPathPrefix.replace(/^\/?/, '').replace(/\/$/, '');
    const syncPath = prefix ? `/${prefix}/auth/sync` : '/auth/sync';
    const popup = window.open(`${adminOrigin}${syncPath}`, '_blank');
    if (!popup) {
      return;
    }

    this.detachAdminAuthSyncListener();

    const timeoutMs = 10 * 60 * 1000;
    let timeoutId: ReturnType<typeof setTimeout>;

    const detach = (): void => {
      window.removeEventListener('message', handler);
      clearTimeout(timeoutId);
      if (this.adminAuthSyncDetach === detach) {
        this.adminAuthSyncDetach = null;
      }
    };

    const handler = (event: MessageEvent): void => {
      if (event.origin !== adminOrigin) {
        return;
      }
      if (event.data?.type !== AUTH_SYNC_MESSAGE_TYPE.REQUEST) {
        return;
      }
      if (event.source !== popup) {
        return;
      }
      const creds = this.getUserData();
      if (!creds?.token) {
        return;
      }
      popup.postMessage({ type: AUTH_SYNC_MESSAGE_TYPE.DELIVER, payload: creds }, adminOrigin);
      detach();
    };

    timeoutId = setTimeout(() => detach(), timeoutMs);
    this.adminAuthSyncDetach = detach;
    window.addEventListener('message', handler);
  }

  /**
   * Opens the AutoFiler SPA (`/auth/sync`) and listens for postMessage so the AutoFiler tab can reuse this Cognito session.
   */
  openAutofilerConsoleWithAuthSync(): void {
    const autofilerOrigin = environment.autofilerAppOrigin.replace(/\/$/, '');
    const prefix = environment.autofilerAppPathPrefix.replace(/^\/?/, '').replace(/\/$/, '');
    const syncPath = prefix ? `/${prefix}/auth/sync` : '/auth/sync';
    const popup = window.open(`${autofilerOrigin}${syncPath}`, '_blank');
    if (!popup) {
      return;
    }

    this.detachAutofilerAuthSyncListener();

    const timeoutMs = 10 * 60 * 1000;
    let timeoutId: ReturnType<typeof setTimeout>;

    const detach = (): void => {
      window.removeEventListener('message', handler);
      clearTimeout(timeoutId);
      if (this.autofilerAuthSyncDetach === detach) {
        this.autofilerAuthSyncDetach = null;
      }
    };

    const handler = (event: MessageEvent): void => {
      if (event.origin !== autofilerOrigin) {
        return;
      }
      if (event.data?.type !== AUTH_SYNC_MESSAGE_TYPE.REQUEST) {
        return;
      }
      if (event.source !== popup) {
        return;
      }
      const creds = this.getUserData();
      if (!creds?.token) {
        return;
      }
      popup.postMessage({ type: AUTH_SYNC_MESSAGE_TYPE.DELIVER, payload: creds }, autofilerOrigin);
      detach();
    };

    timeoutId = setTimeout(() => detach(), timeoutMs);
    this.autofilerAuthSyncDetach = detach;
    window.addEventListener('message', handler);
  }

  private detachAdminAuthSyncListener(): void {
    this.adminAuthSyncDetach?.();
    this.adminAuthSyncDetach = null;
  }

  private detachAutofilerAuthSyncListener(): void {
    this.autofilerAuthSyncDetach?.();
    this.autofilerAuthSyncDetach = null;
  }

  logoutUser(redirectUrl?: string, authenticateThrough: 'login' | 'register' = 'login'): void {
    this.detachAdminAuthSyncListener();
    this.detachAutofilerAuthSyncListener();
    this.analyticsManagerService.sendEvent(EventTypes.LOGOUT, {});
    this.analyticsManagerService.reset();
    defer(() => from(AmplifyAuth.signOut())).subscribe();

    StorageService.clearStoreKeys();
    this.resetAllStores(); 
    this.modalService.dismissAll();

    let navigationOptions: NavigationExtras = {};
    if (redirectUrl) {
      this.redirectUrl = redirectUrl;
      navigationOptions.queryParamsHandling = 'merge'
    }

    this.router.navigate([`/${authenticateThrough}`], navigationOptions);
  }

  logoutWhenTokenExpires() {
    const token = this.getUserData()?.token;
    if (!token) {
      return;
    }
    setTimeout(
      () => this.logoutUser(),
      AuthService.getTimeToTokenExpiry(token)
    );
  }

  /** Routes admins and autofiling operators through `/choose-account`, optionally preserving a deep-link `next` target. */
  private navigateAfterSuccessfulAuth(): void {
    if (this.redirectUrl) {
      const target = this.redirectUrl;
      this.redirectUrl = undefined;
      if (this.isOperatorAccountLandingUser) {
        void this.router.navigate(['/choose-account'], { queryParams: { next: target } });
      } else {
        void this.router.navigateByUrl(target);
      }
      return;
    }
    void this.router.navigateByUrl(this.isOperatorAccountLandingUser ? '/choose-account' : '/dashboard');
  }

  /** AWS Amplify Authentication **/
  verifyEmailAddressToken(email: string, code: string): Observable<SignUpOutput> {
    return defer(() => from(
      AmplifyAuth.confirmSignUp({ username: email, confirmationCode: code })
    ))
      .pipe(
        catchError(this.redirectUnconfirmedUser({email})),
        catchError(this.conceiveDisabledUser()),
        switchMap(() => from(autoSignIn())),
        switchMap(() => this.startNewSession(true)),
        tap({
          next: () => this.navigateAfterSuccessfulAuth(),
          error: () => this.logoutUser()
        }),
        catchError(this.handleError('Verify Email Address', null))
      );
  }

  resendVerificationCode(email: string): Observable<any> {
    return defer(() => from(AmplifyAuth.resendSignUpCode({ username: email })))
      .pipe(
        catchError(this.handleError('Resend Verification Code', null))
      );
  }

  signUpUser(user: Partial<AtomickitUser>): Observable<any> {
    return defer(() => from(AmplifyAuth.signUp({
      username: user.email,
      password: user.password,
      options: {
        userAttributes: {
          email: user.email,
          given_name: user.first_name,
          family_name: user.last_name,
          phone_number: user.phone_number,
          'custom:UserType': user.user_type,
          // Intentionally always Stripe for Cognito; Shopify billing is driven by business profile API.
          'custom:shopifyBilling': '0',
          'custom:UserTrialExpDate': `${moment().add(General.SUBSCRIPTION_TRIAL_PERIOD, 'days').valueOf()}`,
          'custom:currentBizProfileId': '0'
        },
        autoSignIn: true,
      }
    }))
      .pipe(
        catchError(this.handleError('Sign Up User')),
        tap({
          next: () => {
            this.analyticsManagerService.sendEvent(EventTypes.SIGNUP_COMPLETED, {
              name: `${user.first_name} ${user.last_name}`,
              email: user.email
            });
            this.gtmService.trackEvent(GtmEventType.SIGN_UP, {
              user_id: this.businessProfileService.businessProfile?.user_id
            });
            this.metaPixelService.trackCompleteRegistration(user.email);
          }
        })
      ));
  }

  signInUser(user: Partial<AtomickitUser>): Observable<SignInOutput> {
    return defer(() => from(
      AmplifyAuth.signIn({
        username: user.email.toString(),
        password: user.password.toString(),
        ...(AppConfig.NAME === 'local' ? {
          options: {
            authFlowType: 'USER_PASSWORD_AUTH'
          }
        } : {})
      })
    ))
      .pipe(
        catchError(this.redirectUnconfirmedUser(user)),
        catchError(this.conceiveDisabledUser()),
        takeWhile(this.checkForNextSteps(user.email)),
        switchMap(() => this.startNewSession(true)),
        catchError(this.handleError('Sign In')),
        tap({
          next: () => {
            this.analyticsManagerService.sendEvent(EventTypes.LOGIN_SUCCESS, { email: user.email });
            this.navigateAfterSuccessfulAuth();
          },
          error: () => this.logoutUser(),
        }),
      );
  }

  setNewPassword(user: Partial<AtomickitUser>): Observable<any> {
    return from(AmplifyAuth.confirmSignIn({
      challengeResponse: user.password,
      options: {
        userAttributes: {
          given_name: user.first_name,
          family_name: user.last_name,
          phone_number: user.phone_number
        }
      }
    }))
      .pipe(
        catchError(this.conceiveDisabledUser()),
        switchMap(() => this.startNewSession()),
        tap({
          next: () => this.navigateAfterSuccessfulAuth(),
        }),
        catchError(this.handleError('Setup profile'))
      );
  }

  sendPasswordResetCode(email: string): Observable<any> {
    return from(AmplifyAuth.resetPassword({ username: email }))
      .pipe(
        catchError(this.handleError('Request Password Reset Code', null))
      );
  }

  resetPassword({ email, code, password }): Observable<string> {
    return from(AmplifyAuth.confirmResetPassword({ username: email, confirmationCode: code, newPassword: password }))
      .pipe(
        catchError(this.handleError('Reset Password', null)),
        tap( () => this.router.navigateByUrl('/login')),
        tap( () => delete this.passwordResetEmail)
      );
  }

  changePassword({ oldPassword, newPassword }: { oldPassword: string, newPassword: string }): Observable<string> {
    return from(AmplifyAuth.updatePassword({ oldPassword, newPassword }))
      .pipe(
        catchError(this.handleError('Change Password', null))
      );
  }

  updateUserInfo(userInfo: Partial<CognitoUserInterface>): Observable<CognitoUserInterface> {
    return this.updateCognitoUserData(userInfo)
      .pipe(
        catchError(this.handleError('Update User Profile', null)
        )
      );
  }

  switchBusiness(businessProfileId: number | string): Observable<CognitoUserInterface> {
    const updateData = { 'custom:currentBizProfileId': String(businessProfileId) };

    return from(AmplifyAuth.updateUserAttributes({ userAttributes: updateData }))
      .pipe(
        tap(() => this.resetAllStores()),
        switchMap(() => this.startNewSession()),
        catchError(this.handleError('Switch Business', null))
      );
  }

  startNewSession(fromCache = false): Observable<any> {
    return defer(() => from(
      AmplifyAuth.fetchAuthSession({ forceRefresh: !fromCache })
    ))
      .pipe(
        switchMap(this.setUserSession()),
        tap(this.loadBusinessConfig())
      );
  }

  getAuthenticatedUserInfo(): Observable<CognitoUserInterface> {
    // @ts-ignore
    return from(AmplifyAuth.fetchUserAttributes());
  }

  verifyUpdatedEmail(code: string): Observable<Partial<UserInterface>> {
    const updateData = { 'custom:emailChangeRequest': '' };
    return from(AmplifyAuth.confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code }))
      .pipe(
        switchMap(() => this.updateCognitoUserData(updateData)),
        catchError(this.handleError('Verify Updated Email', null))
      );
  }

  cancelEmailVerification(): Observable<Partial<UserInterface>> {
    const updateData = { 'custom:emailChangeRequest': '' };
    return this.updateCognitoUserData(updateData).pipe(
      catchError(this.handleError('Update User Profile', null))
    );
  }

  private loadBusinessConfig() {
    return () => {
      if (this.businessProfileService.businessProfile?.business_profile_id) {
        this.salesTaxConfigService.fetchSalesTaxConfig()
          .subscribe({ error: console.error });
      }
    };
  }

  private loadAndCacheUserData(): Observable<any> {
    return zip(
      this.businessProfileService.bypassCache.getBusinessProfile(),
      this.businessProfileService.bypassCache.getBusinessProfiles(),
      this.referenceDataService.fetchReferenceData()
    ).pipe(
      catchError(() => throwError(() => new Error(
        'Application is currently down, our engineers have been notified and are working on it. Please try again later or visit help.atomictax.com'
      ))),
      tap({ error: () => this.logoutUser() })
    );
  }

  private updateCognitoUserData(updateData: Partial<CognitoUserInterface>) {
    // @ts-ignore
    return from(AmplifyAuth.updateUserAttributes({ userAttributes: updateData }))
      .pipe(
        switchMap(() => this.updateLocalUserData(updateData)
        )
      );
  }

  private updateLocalUserData(data: Partial<CognitoUserInterface>): Observable<Partial<CognitoUserInterface>> {
    const currentUserData = this.getUserData();
    return this.handleAuthSuccess({ ...currentUserData, ...data })
      .pipe(
        switchMap(() => this.getAuthenticatedUserInfo())
      );
  }

  private conceiveDisabledUser() {
    return (err: any) => {
      return err.message === 'User is disabled.'
        ? throwError(() => new Error('Invalid username or password')) // do not disclose
        : throwError(() => err); // continue to handler
    };
  }

  private redirectUnconfirmedUser(user: any) {
    return (err: any) => {
      if (err.message === 'User is not confirmed.') {
        this.router.navigate([`/user-verification/${encodeURIComponent(user.email).replace(/\./g, '%2E')}`]);
      }
      return throwError(() => err); // continue to handler
    };
  }

  private checkForNextSteps(email: string) {
    return (loginData: SignInOutput): boolean => {
      switch (loginData.nextStep.signInStep) {
        case 'DONE':
          return true;

        case 'RESET_PASSWORD':
          this.passwordResetEmail = email;
          this.router.navigateByUrl('/forgot-password');
          return false;

        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          this.router.navigateByUrl('/complete-invite');
          return false;

        case 'CONFIRM_SIGN_UP':
          this.router.navigateByUrl(`/user-verification/${encodeURIComponent(email).replace(/\./g, '%2E')}`);
          return false;

        default:
          return false;
      }
    };
  }
  
  private setSentryUser(user: CognitoUserInterface): void {
    Sentry.setUser({ email: user.email });
  }

  private setUserSession() {
    return ({ tokens }: AuthSession) => {
      if (!tokens) {
        return throwError(() => new Error('No tokens found'));
      }

      const { ...userData } = tokens.idToken.payload as unknown as CognitoUserInterface;

      return this.handleAuthSuccess({
        ...userData,
        token: tokens.idToken.toString()
      })
        .pipe(
          tap(() => this.setSentryUser(userData)),
          tap(() => this.analyticsManagerService.identifyUser(userData))
        );
    };
  }

  private resetAllStores(): void {
    this.store.dispatch(AppActions.ResetAllStores())
  }
}
